// ═══════════════════════════════════════════════
//   KONMAIL, interactive story email client (v3 calendar-locked arc)
//
//   Story arc: 11 real-calendar days, 2026-05-18 → 2026-05-28 (Dokomi May 29).
//   Penalty engine: missed required-reply emails apply hard penalties on
//   first login of the next day. Character silence reactions are encoded as
//   variants on each subsequent email, first matching variant wins.
//   Day 11 resolves an ending from accumulated state vars.
// ═══════════════════════════════════════════════

const { S, addPts, toast, scheduleSave, todayStr, SFX } = window;

// ── Portrait key: maps sender email address → image filename in assets/konmail/ ──
const KM_PORTRAIT_KEY = {
    'orders@freshdaikon.veg':         'freshdaikon',
    'b.croissant@tinisnack.bun':      'tinisnack',
    'inspector@krb.gov.bun':          'krb',
    'bianca@masbunny.bun':            'masbunny',
    'k.fizzywaters@hihihi.bun':       'hihihi',
    'clowri@madriguera.bun':          'clowri',
    'noreply@konbini-auto.bun':       'autologistics',
    'routes@tnma.madriguera':         'tunnelauth',
    'notices@lmec.madriguera':        'electriccoop',
    'h.mossfoot@personal.madriguera': 'hideo',
    'sales@tunnimoss.madriguera':     'tunnimoss',
    'notices@lmha.madriguera':        'healthauth',
    'compliance@lmhsa.madriguera':    'hsa',
    'circular@lmcb.madriguera':       'communityboard',
    'maintenance@s4pm.madriguera':    's4premises',
    'petra.v@personal.madriguera':    'petra',
    'updates@lmptc.madriguera':       'transit',
    'circular@lmspc.madriguera':      'seasonalcoop',
    '[undeliverable return path]':    'emorrow',
};

// ── Session-only state (resets on page reload) ──
let kmFolder = 'inbox';
let kmOpenType = null;
let kmOpenRef  = null;
const kmDailyRead     = new Set();
const kmDailyTrashed  = new Set();
const kmDailyReplied  = new Set();
const kmSelectedItems = new Set(); // entries: 'daily:5' or 'story:s1_intro'
let kmDailyIdx = [];
let kmSpamIdx  = [];
let kmTrash    = [];

// ═══════════════════════════════════════════════
//   v3, Real-calendar day engine
// ═══════════════════════════════════════════════

const KM_DAY_MS = 86400000;

function kmTodayIso() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function kmGameDay() {
    // Optional dev override (set window.__kmDayOverride = N to jump days for testing).
    if (typeof window.__kmDayOverride === 'number') return window.__kmDayOverride;
    const start = new Date(window.KONMAIL_STORY_START + 'T00:00:00');
    const today = new Date(kmTodayIso()       + 'T00:00:00');
    const diff = Math.floor((today.getTime() - start.getTime()) / KM_DAY_MS);
    return diff + 1;   // Day 1 == May 18; days < 1 = pre-game; > 11 = post-game.
}

function kmDateForDay(d) {
    return window.KONMAIL_STORY_DAYS?.[d] || null;
}

// ── Per-email state ('pending' | 'answered' | 'expired' | 'auto-penalised') ──
function kmStoryState(emailId) {
    return (S.mailEmailStates && S.mailEmailStates[emailId]) || 'pending';
}
function kmSetStoryState(emailId, state) {
    if (!S.mailEmailStates) S.mailEmailStates = {};
    S.mailEmailStates[emailId] = state;
}

// ── Match a variant's `when:` clause against current state ──
function kmCheckMatch(when) {
    if (!when) return true;
    if (when.unanswered && kmStoryState(when.unanswered) === 'answered') return false;
    if (when.answered   && kmStoryState(when.answered)   !== 'answered') return false;
    if (when.choice) {
        const made = S.mailStoryChoices?.[when.choice.id];
        if (made === undefined) return false;
        const idx = when.choice.idx;
        const ok = Array.isArray(idx) ? idx.includes(made) : made === idx;
        if (!ok) return false;
    }
    if (when.choiceNot) {
        const made = S.mailStoryChoices?.[when.choiceNot.id];
        const idx = when.choiceNot.idx;
        // If no choice made yet, the "not those indices" condition is satisfied, continue.
        if (made !== undefined) {
            const isAmong = Array.isArray(idx) ? idx.includes(made) : made === idx;
            if (isAmong) return false;
        }
    }
    for (const numKey of ['shopHealth', 'clowAwareness', 'mysteryDepth', 'recordsKept']) {
        const cond = when[numKey];
        if (!cond) continue;
        const sKey = 'mail' + numKey.charAt(0).toUpperCase() + numKey.slice(1);
        const v = S[sKey] || 0;
        if (cond.gte !== undefined && v <  cond.gte) return false;
        if (cond.lte !== undefined && v >  cond.lte) return false;
        if (cond.gt  !== undefined && v <= cond.gt ) return false;
        if (cond.lt  !== undefined && v >= cond.lt ) return false;
    }
    // Voice counter predicates (writing spec v1). Same shape as numeric mail* checks.
    const VOICE_KEYS = {
        voiceWarm: 'playerVoiceWarm',
        voiceCurious: 'playerVoiceCurious',
        voiceProfessional: 'playerVoiceProfessional',
        voiceClipped: 'playerVoiceClipped',
    };
    for (const wKey of Object.keys(VOICE_KEYS)) {
        const cond = when[wKey];
        if (!cond) continue;
        const v = S[VOICE_KEYS[wKey]] || 0;
        if (cond.gte !== undefined && v <  cond.gte) return false;
        if (cond.lte !== undefined && v >  cond.lte) return false;
        if (cond.gt  !== undefined && v <= cond.gt ) return false;
        if (cond.lt  !== undefined && v >= cond.lt ) return false;
    }
    // Boolean story flags. Use `=== true` / `=== false` to match strictness.
    for (const bKey of [
        'eMorrowSurfaced', 'documentedPath', 'hideoRouteSurfaced', 'clippedRecoveryActive',
        'record_package', 'record_tall_customer', 'record_emorrow', 'record_hideo_ps', 'record_corridors',
        'hideoRomance', 'petraRomance',
    ]) {
        if (when[bKey] === undefined) continue;
        if (!!S[bKey] !== !!when[bKey]) return false;
    }
    // Package path: 'opened' | 'documented' | 'left' | null
    if (when.packagePath !== undefined && (S.packagePath || null) !== when.packagePath) {
        return false;
    }
    // Writing spec v5, drops recovery arc. dropsPulled is boolean; dropsPulledBy
    // is an enum string ('player_beat1' | 'player_beat2' | 'coneja' | null) and
    // accepts either a single value or an array of accepted values.
    if (when.dropsPulled !== undefined && !!S.dropsPulled !== !!when.dropsPulled) {
        return false;
    }
    if (when.dropsPulledBy !== undefined) {
        const want = when.dropsPulledBy;
        if (Array.isArray(want)) {
            if (!want.includes(S.dropsPulledBy)) return false;
        } else if (S.dropsPulledBy !== want) {
            return false;
        }
    }
    // Ending id once locked at d09 close. Lets d11 variants key off it.
    if (when.endingId !== undefined && (S.mailEnding || null) !== when.endingId) {
        return false;
    }
    // staffConcern (writing spec v2 §12), numeric predicate, same shape as mail* numeric checks.
    if (when.staffConcern) {
        const cond = when.staffConcern;
        const v = S.staffConcern || 0;
        if (cond.gte !== undefined && v <  cond.gte) return false;
        if (cond.lte !== undefined && v >  cond.lte) return false;
        if (cond.gt  !== undefined && v <= cond.gt ) return false;
        if (cond.lt  !== undefined && v >= cond.lt ) return false;
    }
    for (const tKey of ['trustHideo', 'trustPetra']) {
        if (when[tKey] !== undefined && S['mail' + tKey.charAt(0).toUpperCase() + tKey.slice(1)] !== when[tKey]) {
            return false;
        }
    }
    if (when.inspectorFailed === true) {
        const fa = kmStoryState('d04_inspector_checklist') === 'auto-penalised';
        const fb = kmStoryState('d08_inspector_reminder')  === 'auto-penalised';
        if (!(fa || fb)) return false;
    }
    return true;
}

function kmResolveVariant(email) {
    if (!email.variants) return null;
    for (const v of email.variants) {
        if (kmCheckMatch(v.when)) return v;
    }
    return null;
}

function kmEffectiveBody(email) {
    const v = kmResolveVariant(email);
    let body = (v && v.body) || email.body || '';
    // Writing spec v2 §14, d10_endshift gets staff notes inserted when
    // staffConcern >= 2. Composed at render time so it applies on top of
    // any packagePath variant (rather than duplicating each variant body).
    if (email.id === 'd10_endshift' && (S.staffConcern || 0) >= 2) {
        body = body.replace(
            'Building manager has not responded to my notice about the lights.\n\n- Lib',
            'Building manager has not responded to my notice about the lights.\n\nNote: Clow contacted me directly this week to verify several supplier orders. I sent the records she requested. Standard procedure.\n\n- Lib'
        );
        body = body.replace(
            'ok so lib already said the important stuff. tall coat guy is back. lights still doing the thing. customers still asking for drops we apparently never sold.\n\nsome other stuff:',
            "ok so lib already said the important stuff. tall coat guy is back. lights still doing the thing. customers still asking for drops we apparently never sold.\n\nbtw clow asked me twice this week if i was sure everything was okay. i told her what i saw. just so you know ✦ not trying to throw you under the bus or anything, she's just my boss too and she's worried.\n\nanyway...\n\nsome other stuff:"
        );
    }
    return body;
}
function kmEffectiveChoices(email) {
    const v = kmResolveVariant(email);
    const raw = (v && v.choices) || email.choices || [];
    // v3 §19, per-choice `requires:` gate. Same predicate language as variant
    // `when:` (kmCheckMatch). Used to gate the d04 romance opt-in to high
    // trust. Choices without `requires:` always show.
    return raw.filter(c => !c.requires || kmCheckMatch(c.requires));
}

// ── Writing spec v2 §15, mailRecordsKept is derived from five named booleans.
function kmRecomputeRecordsKept() {
    S.mailRecordsKept =
        (S.record_package        ? 1 : 0) +
        (S.record_tall_customer  ? 1 : 0) +
        (S.record_emorrow        ? 1 : 0) +
        (S.record_hideo_ps       ? 1 : 0) +
        (S.record_corridors      ? 1 : 0);
}

// ── Writing spec v2 §12, staffConcern one-time event tracking. Each event key
//    fires at most once. The 'autopen:*' bucket has a global cap of +3 (the
//    staff can't keep escalating forever, Clow gets the picture).
function kmRecordStaffConcernEvent(key, delta) {
    if (!S.staffConcernEvents) S.staffConcernEvents = new Set();
    if (S.staffConcernEvents.has(key)) return;
    if (key.startsWith('autopen:')) {
        const autopenCount = [...S.staffConcernEvents].filter(k => k.startsWith('autopen:')).length;
        // Always mark the key fired so we don't reconsider it, but only credit
        // toward staffConcern up to the cap.
        S.staffConcernEvents.add(key);
        if (autopenCount >= 3) return;
        S.staffConcern = (S.staffConcern || 0) + (delta || 1);
        return;
    }
    S.staffConcernEvents.add(key);
    S.staffConcern = (S.staffConcern || 0) + (delta || 1);
}

// ── Apply a choice's effects to S.mail* and award pts ──
function kmApplyEffects(effects, reason) {
    if (!effects) return 0;
    let pts = 0;
    if (effects.shopHealth) {
        // Detect downward crossings of 60 and 40, staff would notice and tell Clow.
        const before = S.mailShopHealth || 0;
        S.mailShopHealth = Math.max(0, Math.min(100, before + effects.shopHealth));
        const after = S.mailShopHealth;
        if (before >= 60 && after < 60) kmRecordStaffConcernEvent('hp_below_60', 1);
        if (before >= 40 && after < 40) kmRecordStaffConcernEvent('hp_below_40', 2);
    }
    if (effects.clowAwareness) S.mailClowAwareness = (S.mailClowAwareness || 0) + effects.clowAwareness;
    if (effects.mysteryDepth)  S.mailMysteryDepth  = (S.mailMysteryDepth  || 0) + effects.mysteryDepth;
    // recordsKept integer effects (v1) are no longer applied directly, the count
    // is derived from the five record_* booleans (v2 §15). Choice effects that
    // formerly set recordsKept should set the specific record_* boolean instead.
    if (effects.trustHideo)    S.mailTrustHideo    = effects.trustHideo;
    if (effects.trustPetra)    S.mailTrustPetra    = effects.trustPetra;
    // Voice deltas. Clipped is gated by clippedRecoveryActive: once a recovery
    // choice is taken (d06 v5/C or d09 v5/C), playerVoiceClipped stops
    // incrementing but keeps its historical value.
    if (effects.voiceWarm)         S.playerVoiceWarm         = (S.playerVoiceWarm         || 0) + effects.voiceWarm;
    if (effects.voiceCurious)      S.playerVoiceCurious      = (S.playerVoiceCurious      || 0) + effects.voiceCurious;
    if (effects.voiceProfessional) S.playerVoiceProfessional = (S.playerVoiceProfessional || 0) + effects.voiceProfessional;
    if (effects.voiceClipped && !S.clippedRecoveryActive) {
        S.playerVoiceClipped = (S.playerVoiceClipped || 0) + effects.voiceClipped;
    }
    // Boolean story flags, set-only (never unset by a choice).
    if (effects.eMorrowSurfaced) {
        S.eMorrowSurfaced = true;
        // §15, surfacing E. Morrow to Clow is record_emorrow's set condition.
        S.record_emorrow = true;
    }
    if (effects.documentedPath)      S.documentedPath      = true;
    if (effects.hideoRouteSurfaced)  S.hideoRouteSurfaced  = true;
    if (effects.clippedRecovery)     S.clippedRecoveryActive = true;
    // v3 §19, Hideo romance opt-in is set-only and gated to trustHideo === 'high'
    // at the choice-availability layer (kmEffectiveChoices `requires:`). Never unset.
    if (effects.hideoRomance)        S.hideoRomance        = true;
    // v6 §01, Petra romance opt-in. Same shape; gated to trustPetra === 'high'
    // at the choice-availability layer on d07_petra_followup.
    if (effects.petraRomance)        S.petraRomance        = true;
    // Named investigative records (v2 §15). Setting any of them triggers a
    // recompute of S.mailRecordsKept so downstream predicates read the new count.
    let recordsChanged = false;
    for (const r of ['record_package', 'record_tall_customer', 'record_emorrow', 'record_hideo_ps', 'record_corridors']) {
        if (effects[r] && !S[r]) { S[r] = true; recordsChanged = true; }
    }
    if (recordsChanged) kmRecomputeRecordsKept();
    // Package path: d06_coneja_package sets one of 'opened' | 'documented' | 'left'.
    if (effects.packagePath)         S.packagePath         = effects.packagePath;
    // Writing spec v5, drops recovery arc. Both set-only (the player can't
    // un-pull them, and once intervened the source, beat1/beat2/coneja, sticks).
    if (effects.dropsPulled === true) S.dropsPulled = true;
    if (effects.dropsPulledBy && !S.dropsPulledBy) S.dropsPulledBy = effects.dropsPulledBy;
    // staffConcern direct increment (used by reply choices, e.g. d01 dismissive,
    // d06 shove-back, the d06 staff-concerned variant's lie choice).
    if (effects.staffConcern)        S.staffConcern        = (S.staffConcern || 0) + effects.staffConcern;
    if (effects.pts != null)   pts = effects.pts;
    if (pts) addPts(pts, reason);
    return pts;
}

// ── Day-9 ending lock (writing spec v2 §16, Shop Health is the spine).
//    Computed once when d09_clow_checkin3 closes (answered OR expired).
//    Result is written to S.mailEnding so d11_clow_ending can key off endingId.
function kmComputeEndingId() {
    const hp    = S.mailShopHealth    || 0;
    const aware = S.mailClowAwareness || 0;
    const myst  = S.mailMysteryDepth  || 0;
    const recs  = S.mailRecordsKept   || 0;
    // PRIORITY 1: Shop dead → closed regardless. The player let the konbini fail;
    // no amount of warmth, disclosure, or investigation rescues that.
    if (hp <= 40) return 'closed';
    // PRIORITY 2: Documented (secret). Healthy shop AND the detective work;
    // investigating while the shop dies does NOT earn this ending.
    if ((recs >= 5 || S.documentedPath === true) && hp >= 60) return 'documented';
    // PRIORITY 3: Everything In Order. The good shopkeeper ending, high health,
    // light mystery footprint, warm with Clow, operational items resolved.
    // (goroConfirmed / hideoPeachesConfirmed / inspectorPassed are spec-listed
    //  flags; they don't all exist in code yet, so we treat absence as falsy and
    //  the player simply doesn't pass this gate. That matches v2 §16 intent:
    //  Good is the hardest non-secret ending, and it should be hard.)
    if (hp >= 75 && aware >= 2 && myst <= 2 &&
        S.goroConfirmed === true &&
        S.hideoPeachesConfirmed === true &&
        S.inspectorPassed === true) {
        return 'good';
    }
    // PRIORITY 4: The Konbini That Remembers. You did the work AND something
    // strange happened. You let it.
    if (hp >= 60 && myst >= 3 && myst <= 4) return 'remembers';
    // PRIORITY 5: Clipped-player branch. Sustained distance is its own thing.
    if ((S.playerVoiceClipped || 0) >= 5) {
        if (hp >= 40 && hp <= 60) return 'space';
        return 'closed';
    }
    // PRIORITY 6: Space Between Shelves, the honest middle.
    if (hp >= 40 && hp <= 60 && myst >= 3 && aware <= 1) return 'space';
    // FALLBACK: Temporarily Closed.
    return 'closed';
}

function kmLockEndingIfNeeded() {
    if (S.mailEnding) return;     // already locked
    const state = kmStoryState('d09_clow_checkin3');
    if (state !== 'answered' && state !== 'expired' && state !== 'auto-penalised') return;
    S.mailEnding = kmComputeEndingId();
    scheduleSave();
}

// ── Late-login / catch-up: apply per-day penalties for missed days (§2 of the lore bible).
//    Rule: −8 Shop Health per missed day with at least one required-reply email,
//          −5 Shop Health per missed day with none.
//    Per-email missedPenalty (e.g. Cassian's −5) is handled by kmExpireSameDay, which
//    fires for same-day expiries and for emails the player simply ignored on their
//    active day. This avoids double-stacking for fully-missed days.
function kmCatchUp() {
    const today = kmGameDay();

    if (today >= 1 && today <= 11 && S.mailFirstLoginDay == null) {
        S.mailFirstLoginDay = today;
    }

    const lastDay = S.mailLastActiveDay || 0;
    if (today <= lastDay) {
        if (kmExpireSameDay(today)) scheduleSave();
        return;
    }

    const story    = window.KONMAIL_STORY || [];
    const firstLog = S.mailFirstLoginDay || today;
    let dayLoss    = 0;
    let penalisedCount = 0;

    // Walk each missed day strictly before `today`.
    for (let d = Math.max(1, lastDay + 1); d < today && d <= 11; d++) {
        if (d === firstLog) continue;             // don't penalise the day they first logged in
        const dayEmails    = story.filter(e => e.day === d);
        const requiredOnDay = dayEmails.filter(e => e.requiresReply);
        const anyAnswered  = requiredOnDay.some(e => kmStoryState(e.id) === 'answered');
        if (requiredOnDay.length > 0 && !anyAnswered) {
            dayLoss += 8;
        } else if (requiredOnDay.length === 0) {
            dayLoss += 5;
        }
        for (const e of dayEmails) {
            const state = kmStoryState(e.id);
            // 'arrived' counts as pending, fall through to expiry.
            if (state === 'answered' || state === 'expired' || state === 'auto-penalised') continue;
            kmSetStoryState(e.id, e.requiresReply ? 'auto-penalised' : 'expired');
            if (e.requiresReply) {
                penalisedCount++;
                // Writing spec v2 §12, staff notice the auto-penalised email.
                kmRecordStaffConcernEvent('autopen:' + e.id, 1);
                if (e.id === 'd07_goro_crackers' || e.id === 'd05_goro' || e.id === 'd09_goro_ultimatum') {
                    kmRecordStaffConcernEvent('goro_silent', 1);
                }
                if (e.id === 'd04_bianca' || e.id === 'd10_bianca_confirm') {
                    kmRecordStaffConcernEvent('bianca_declined', 1);
                }
            }
        }
    }

    if (dayLoss > 0) {
        // Route through kmApplyEffects so HP crossings fire staff events.
        kmApplyEffects({ shopHealth: -dayLoss }, 'mail:catchup:dayloss');
    }

    // Writing spec v2 §12, Inspector checklist deadline. If we've crossed past
    // Day 8 and d04_inspector_checklist is still pending (or auto-pen'd), staff
    // notice that the checklist never went out. Fires once.
    if (today >= 8 && kmStoryState('d04_inspector_checklist') !== 'answered') {
        kmRecordStaffConcernEvent('inspector_deadline', 1);
    }

    S.mailLastActiveDay = today;
    scheduleSave();
    kmExpireSameDay(today);
    // After expiries settle, if d09 has now closed (expired/auto-penalised),
    // lock the ending so d11 reads off a stable S.mailEnding.
    kmLockEndingIfNeeded();

    if (penalisedCount > 0) {
        toast(`✉ ${penalisedCount} email${penalisedCount > 1 ? 's' : ''} unanswered while you were away. Shop Health −${dayLoss}.`);
    }
}

// ── Arrival effects: apply a one-time Shop Health (etc) change when an email
//    becomes visible. Used by emails whose mere arrival is the consequence
//    (Goro Day 9, Inspector outcome). Variants can override the base effect.
//    Tracked via `S.mailEmailStates[id] === 'arrived'` (one-shot marker that
//    still reads as "unanswered" for variants and reply-window logic).
function kmApplyArrivalEffects() {
    const story = window.KONMAIL_STORY || [];
    let mutated = false;
    for (const e of story) {
        if (!kmIsStoryUnlocked(e)) continue;
        const state = kmStoryState(e.id);
        if (state !== 'pending') continue;     // already arrived/answered/expired
        // Variant arrival effect wins over base.
        const v = kmResolveVariant(e);
        const effect = (v && v.arrivalEffect) || e.arrivalEffect;
        const toastMsg = (v && v.arrivalToast) || e.arrivalToast;
        if (!effect) continue;
        kmApplyEffects(effect, `mail:arrival:${e.id}`);
        kmSetStoryState(e.id, 'arrived');
        mutated = true;
        if (toastMsg) toast(toastMsg);
    }
    if (mutated) scheduleSave();
}

// Handle emails with `expiresOnDay` that have already passed end-of-day.
// Returns true if anything mutated, so the caller can decide whether to scheduleSave.
function kmExpireSameDay(today) {
    const story = window.KONMAIL_STORY || [];
    let mutated = false;
    for (const e of story) {
        if (!e.expiresOnDay) continue;
        if (today <= e.expiresOnDay) continue;
        const state = kmStoryState(e.id);
        // 'arrived' (had arrival effect applied) is still actionable until expiry.
        if (state === 'answered' || state === 'expired' || state === 'auto-penalised') continue;
        if (e.requiresReply) {
            const pen = e.missedPenalty || { shopHealth: -8 };
            if (pen.shopHealth) {
                // Use the same path as kmApplyEffects so HP crossings fire staff events.
                kmApplyEffects({ shopHealth: pen.shopHealth }, `mail:expire:${e.id}`);
            }
            kmSetStoryState(e.id, 'auto-penalised');
            // Writing spec v2 §12, staff notice the auto-penalised email.
            kmRecordStaffConcernEvent('autopen:' + e.id, 1);
            // Named-event extras (still capped to one increment each).
            if (e.id === 'd07_goro_crackers' || e.id === 'd05_goro' || e.id === 'd09_goro_ultimatum') {
                kmRecordStaffConcernEvent('goro_silent', 1);
            }
            if (e.id === 'd04_bianca' || e.id === 'd10_bianca_confirm') {
                kmRecordStaffConcernEvent('bianca_declined', 1);
            }
        } else {
            kmSetStoryState(e.id, 'expired');
        }
        mutated = true;
    }
    return mutated;
}

// ═══════════════════════════════════════════════
//   Spam helpers (unchanged from prior version)
// ═══════════════════════════════════════════════
function kmSpamFindAny(id) {
    return window.KONMAIL_SPAM_POOL.find(e => e.id === id)
        || window.KONMAIL_SPAM_CHAINS.find(e => e.id === id)
        || null;
}
function kmIsChainUnlocked(chain) {
    const u = chain.unlock;
    if (!u) return true;
    if (u.type === 'spamChoice') {
        const made = (S.mailSpamState.repliedChoices || {})[u.id];
        if (made === undefined) return false;
        return Array.isArray(u.idx) ? u.idx.includes(made) : made === u.idx;
    }
    return false;
}
function kmVisibleSpamIds() {
    const ss = S.mailSpamState;
    const trashed = new Set(ss.trashedIds || []);
    const ids = new Set();
    for (const i of kmSpamIdx) ids.add(window.KONMAIL_SPAM_POOL[i].id);
    for (const id of Object.keys(ss.repliedChoices || {})) ids.add(id);
    for (const id of (ss.unlockedChainIds || [])) ids.add(id);
    for (const c of window.KONMAIL_SPAM_CHAINS) {
        if (kmIsChainUnlocked(c)) ids.add(c.id);
    }
    for (const t of trashed) ids.delete(t);
    return [...ids];
}

// ═══════════════════════════════════════════════
//   Daily-roll helpers (unchanged)
// ═══════════════════════════════════════════════
function kmDateSeed(s) {
    let h = 0;
    for (const c of s) h = ((h * 31) + c.charCodeAt(0)) & 0xffffffff;
    return Math.abs(h);
}
function kmSeededPick(max, count, seed) {
    const a = Array.from({ length: max }, (_, i) => i);
    let s = seed;
    for (let i = a.length - 1; i > 0; i--) {
        s = ((s * 1664525) + 1013904223) & 0xffffffff;
        const j = Math.abs(s) % (i + 1);
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, Math.min(count, max));
}
function kmSeededPickFrom(arr, count, seed) {
    const a = [...arr];
    let s = seed;
    for (let i = a.length - 1; i > 0; i--) {
        s = ((s * 1664525) + 1013904223) & 0xffffffff;
        const j = Math.abs(s) % (i + 1);
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, Math.min(count, a.length));
}

// ═══════════════════════════════════════════════
//   Story unlock (date-based) + visible-in-inbox check
// ═══════════════════════════════════════════════
function kmIsStoryUnlocked(email) {
    const u = email.unlock || { type:'date', day: email.day };
    if (u.type === 'date') {
        const gameDay = kmGameDay();
        if (gameDay < (u.day || 0)) return false;
        if (u.requires) {
            // Writing spec v10 §03, OR-of-predicates gate. Each item is a
            // `when:`-style block evaluated through kmCheckMatch (so `choice`,
            // `answered`, numeric checks etc. all work inside it). Used by
            // d11_barnabe to fire if EITHER the d03 or d06 name-connection
            // choice was made.
            if (u.requires.anyOf) {
                const branches = Array.isArray(u.requires.anyOf) ? u.requires.anyOf : [u.requires.anyOf];
                if (!branches.some(b => kmCheckMatch(b))) return false;
            }
            // Writing spec v11, bare `choice:` predicate at the unlock layer.
            // Mirrors variant `when.choice` (see kmCheckMatch). Used by
            // d11_hiroshi (d07 idx 1) and d11_cassian (d05 idx 0).
            if (u.requires.choice) {
                const made = S.mailStoryChoices?.[u.requires.choice.id];
                if (made === undefined) return false;
                const idx = u.requires.choice.idx;
                const ok = Array.isArray(idx) ? idx.includes(made) : made === idx;
                if (!ok) return false;
            }
            for (const k of ['recordsKept', 'mysteryDepth', 'clowAwareness', 'shopHealth']) {
                const cond = u.requires[k];
                if (!cond) continue;
                const sKey = 'mail' + k.charAt(0).toUpperCase() + k.slice(1);
                const v = S[sKey] || 0;
                if (cond.gte !== undefined && v < cond.gte) return false;
                if (cond.lte !== undefined && v > cond.lte) return false;
            }
            if (u.requires.unanswered) {
                const ids = Array.isArray(u.requires.unanswered) ? u.requires.unanswered : [u.requires.unanswered];
                for (const id of ids) {
                    if (kmStoryState(id) === 'answered') return false;
                }
            }
            if (u.requires.answered) {
                const ids = Array.isArray(u.requires.answered) ? u.requires.answered : [u.requires.answered];
                for (const id of ids) {
                    if (kmStoryState(id) !== 'answered') return false;
                }
            }
            // Writing spec v5, gate visibility on package/drops state. Same
            // shape as the variant `when` clauses so the predicate language
            // reads consistently across data-konmail.js.
            if (u.requires.packagePath !== undefined
                && (S.packagePath || null) !== u.requires.packagePath) {
                return false;
            }
            if (u.requires.dropsPulled !== undefined
                && !!S.dropsPulled !== !!u.requires.dropsPulled) {
                return false;
            }
            if (u.requires.dropsPulledBy !== undefined) {
                const want = u.requires.dropsPulledBy;
                if (Array.isArray(want)) {
                    if (!want.includes(S.dropsPulledBy)) return false;
                } else if (S.dropsPulledBy !== want) {
                    return false;
                }
            }
        }
        return true;
    }
    return true;
}

function kmUnreadInbox() {
    const story = (window.KONMAIL_STORY || []).filter(e =>
        kmIsStoryUnlocked(e) && !S.mailStoryRead.includes(e.id) && !S.mailStoryTrashed.includes(e.id)
    );
    const dailyUnread = kmDailyIdx.filter(i => !kmDailyRead.has(i) && !kmDailyTrashed.has(i)).length;
    return story.length + dailyUnread;
}

function kmUnreadSpam() {
    const spamRead = new Set(S.mailSpamState.readIds || []);
    return kmVisibleSpamIds().filter(id => !spamRead.has(id)).length;
}

function kmGenToday() {
    const today  = todayStr();
    const ds     = S.mailDailyState;
    const pool   = window.KONMAIL_DAILY_POOL;
    const spPool = window.KONMAIL_SPAM_POOL;

    if (ds.date !== today) {
        const seed    = kmDateSeed(today);
        const history = new Set(ds.history || []);
        const avail   = pool.map((_, i) => i).filter(i => !history.has(pool[i].id));
        const pickFrom = avail.length >= 4 ? avail : pool.map((_, i) => i);

        const todayIdx     = kmSeededPickFrom(pickFrom, 4, seed);
        const todaySpamIdx = kmSeededPick(spPool.length, 3, seed ^ 0x5a5a5a5a);
        const newHistory   = [...(ds.history || []), ...todayIdx.map(i => pool[i].id)].slice(-pool.length);

        S.mailDailyState = {
            date:       today,
            ids:        todayIdx.map(i => pool[i].id),
            spamIds:    todaySpamIdx.map(i => spPool[i].id),
            history:    newHistory,
            readIds:      [],
            trashedIds:   [],
            repliedChoices: {},
        };
        scheduleSave();
    }

    kmDailyIdx = (S.mailDailyState.ids || [])
        .map(id => pool.findIndex(e => e.id === id)).filter(i => i >= 0);
    kmSpamIdx  = (S.mailDailyState.spamIds || [])
        .map(id => spPool.findIndex(e => e.id === id)).filter(i => i >= 0);

    kmDailyRead.clear();
    kmDailyTrashed.clear();
    kmDailyReplied.clear();
    for (const id of (S.mailDailyState.readIds || [])) {
        const i = pool.findIndex(e => e.id === id); if (i >= 0) kmDailyRead.add(i);
    }
    for (const id of (S.mailDailyState.trashedIds || [])) {
        const i = pool.findIndex(e => e.id === id); if (i >= 0) kmDailyTrashed.add(i);
    }
    for (const id of Object.keys(S.mailDailyState.repliedChoices || {})) {
        const i = pool.findIndex(e => e.id === id); if (i >= 0) kmDailyReplied.add(i);
    }
}

// ── Open / navigate ──

function openKonmail() {
    if (!S.mailFirstOpen) {
        S.mailFirstOpen = new Date().toISOString();
        scheduleSave();
    }
    kmGenToday();
    kmCatchUp();              // apply v3 penalties for any days missed
    kmApplyArrivalEffects();  // apply one-time arrival penalties (Goro D9, Inspector outcome)
    kmFolder   = 'inbox';
    kmOpenType = null;
    kmOpenRef  = null;
    window.openWin('w-konmail');
    document.querySelectorAll('.km-folder-btn').forEach(b => b.classList.remove('active'));
    document.getElementById('km-fold-inbox')?.classList.add('active');
    kmRenderPortrait(null);
    kmRender();
}

function kmSetFolder(f) {
    kmFolder   = f;
    kmOpenType = null;
    kmOpenRef  = null;
    kmSelectedItems.clear();
    document.querySelectorAll('.km-folder-btn').forEach(b => b.classList.remove('active'));
    document.getElementById('km-fold-' + f)?.classList.add('active');
    kmRenderPortrait(null);
    kmRender();
}

function kmBack() {
    kmOpenType = null;
    kmOpenRef  = null;
    kmRenderPortrait(null);
    kmRender();
}

function kmRender() {
    kmUpdateBadge();
    if (kmOpenType) kmRenderDetail();
    else            kmRenderList();
}

function kmUpdateBadge() {
    const inbox = kmUnreadInbox();
    const spam  = kmUnreadSpam();
    const setBadge = (id, n) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.textContent = n > 0 ? String(n) : '';
        el.style.display = n > 0 ? '' : 'none';
    };
    setBadge('km-badge',       inbox);
    setBadge('km-badge-spam',  spam);
    setBadge('km-icon-badge',  inbox + spam);
}

// Refresh the desktop-icon badge without opening the window.
function kmRefreshBadges() {
    if (!window.KONMAIL_DAILY_POOL || !window.KONMAIL_SPAM_POOL) return;
    kmGenToday();
    kmUpdateBadge();
}

function kmRenderPortrait(email) {
    const box = document.getElementById('km-portrait-inner');
    if (!box) return;
    if (!email) {
        box.style.background = 'var(--blue)';
        const logoImg = document.createElement('img');
        logoImg.src = 'assets/icons/logo.png';
        logoImg.alt = 'Konbini Clowri';
        logoImg.style.cssText = 'width:78%;height:78%;object-fit:contain;opacity:0.85;';
        logoImg.onerror = () => { box.innerHTML = '<span style="font-size:30px;opacity:0.3">✉</span>'; };
        box.innerHTML = '';
        box.appendChild(logoImg);
        return;
    }
    box.style.background = email.portraitColor || 'var(--blue)';
    const img = document.createElement('img');
    const portraitKey = KM_PORTRAIT_KEY[email.email] || email.id;
    img.src = `assets/konmail/${portraitKey}.png`;
    img.alt = email.from;
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
    img.onerror = () => {
        box.innerHTML = `<span style="font-size:38px">${email.portraitEmoji || '📧'}</span>`;
    };
    box.innerHTML = '';
    box.appendChild(img);
}

// ═══════════════════════════════════════════════
//   Status bar, small inline display of v3 state vars (informational, not punitive)
// ═══════════════════════════════════════════════
function kmRenderStatusBar() {
    const gameDay = kmGameDay();
    if (gameDay < 1 || gameDay > 11) return '';
    const dateStr = kmDateForDay(gameDay) || '';
    const hp = S.mailShopHealth || 0;
    const hpClass = hp >= 70 ? 'good' : hp >= 40 ? 'mid' : 'low';
    const trustLabel = (v) => v === 'high' ? '◆◆◆' : v === 'mid' ? '◆◆' : '◆';
    return `<div class="km-status-bar">
        <div class="km-status-day">DAY ${gameDay}/11 · ${dateStr}</div>
        <div class="km-status-stats">
            <span class="km-stat km-stat-${hpClass}" title="Shop Health">♥ ${hp}</span>
            <span class="km-stat" title="Records Kept">📋 ${S.mailRecordsKept || 0}</span>
            <span class="km-stat" title="Mystery Depth">❓ ${S.mailMysteryDepth || 0}</span>
            <span class="km-stat" title="Clow Awareness">✦ ${S.mailClowAwareness || 0}</span>
            <span class="km-stat" title="Trust: Hideo">🍑 ${trustLabel(S.mailTrustHideo)}</span>
            <span class="km-stat" title="Trust: Petra">🍵 ${trustLabel(S.mailTrustPetra)}</span>
        </div>
    </div>`;
}

// ═══════════════════════════════════════════════
//   List view
// ═══════════════════════════════════════════════
function kmRenderList() {
    const panel = document.getElementById('km-panel');
    if (!panel) return;
    let rows = '';
    const gameDay = kmGameDay();

    if (kmFolder === 'inbox') {
        const items = [];
        const stories = (window.KONMAIL_STORY || []).filter(e =>
            kmIsStoryUnlocked(e) && !S.mailStoryTrashed.includes(e.id)
        );
        for (const e of stories) {
            const state = kmStoryState(e.id);
            items.push({
                kind: 'story', e, ref: e.id,
                read: S.mailStoryRead.includes(e.id),
                state,
                missed: state === 'expired' || state === 'auto-penalised',
                isEnding: !!e.ending,
                day: e.day,
                arrived: kmDateForDay(e.day),
            });
        }
        for (const i of kmDailyIdx) {
            if (kmDailyTrashed.has(i)) continue;
            items.push({ kind: 'daily', e: window.KONMAIL_DAILY_POOL[i], ref: i, read: kmDailyRead.has(i) });
        }
        // Sort: ending email first (Day 11), then unread missed-required-warnings, then unread normal,
        // then read. Stable within groups.
        items.sort((a, b) => {
            if (a.isEnding && !b.isEnding) return -1;
            if (b.isEnding && !a.isEnding) return  1;
            const aRead = a.read ? 1 : 0;
            const bRead = b.read ? 1 : 0;
            return aRead - bRead;
        });

        for (const it of items) {
            const refArg = it.kind === 'daily' ? it.ref : `'${it.ref}'`;
            const selKey = `${it.kind}:${it.ref}`;
            const checked = kmSelectedItems.has(selKey) ? ' checked' : '';
            // Day-11 ending must not be trashable or bulk-selectable.
            const checkbox = it.isEnding
                ? `<span class="km-row-check km-check-disabled" title="Ending email"></span>`
                : `<input type="checkbox" class="km-row-check" onclick="event.stopPropagation(); kmToggleSelect('${it.kind}',${refArg})"${checked} aria-label="Select email">`;
            const trashBtn = it.isEnding
                ? ''
                : `<button class="km-row-trash" onclick="event.stopPropagation(); kmMoveToTrash('${it.kind}',${refArg})" aria-label="Move to trash" title="Move to trash">🗑</button>`;
            const cls = [
                'km-row',
                it.read ? 'km-read' : 'km-unread',
                it.missed ? 'km-missed' : '',
                it.isEnding ? 'km-ending-row' : '',
            ].filter(Boolean).join(' ');
            const status = it.isEnding ? 'TODAY ✦' : it.missed ? 'MISSED' : (it.read ? 'READ' : 'UNREAD');
            const arrivedTag = it.kind === 'story' && it.arrived
                ? `<span class="km-arrived" title="Arrived ${it.arrived}">${it.arrived.slice(5)}</span>`
                : '';
            rows += `<div class="${cls}" onclick="kmOpenEmail('${it.kind}',${refArg})">
                ${checkbox}
                <span class="km-status">${status}</span>
                <span class="km-row-from">${it.e.from}</span>
                <span class="km-row-sub">${it.e.subject}</span>
                ${arrivedTag}
                ${trashBtn}
            </div>`;
        }
    } else if (kmFolder === 'spam') {
        const readSet = new Set(S.mailSpamState.readIds || []);
        for (const id of kmVisibleSpamIds()) {
            const e = kmSpamFindAny(id);
            if (!e) continue;
            const read = readSet.has(id);
            rows += `<div class="km-row${read ? ' km-read' : ' km-unread'}" onclick="kmOpenEmail('spam','${id}')">
                <span class="km-status">${read ? 'READ' : 'UNREAD'}</span>
                <span class="km-row-from">${e.from}</span>
                <span class="km-row-sub">${e.subject}</span>
            </div>`;
        }
    } else if (kmFolder === 'trash') {
        for (const t of kmTrash) {
            rows += `<div class="km-row km-read" onclick="kmOpenEmail('trash','${t.trashId}')">
                <span class="km-status">TRASH</span>
                <span class="km-row-from">${t.from}</span>
                <span class="km-row-sub">${t.subject}</span>
            </div>`;
        }
    }

    const statusBar = (kmFolder === 'inbox') ? kmRenderStatusBar() : '';
    const checkColGap = kmFolder === 'inbox' ? '<span class="km-col-check-gap"></span>' : '';
    const colHeader = `<div class="km-col-header">
        ${checkColGap}
        <span class="km-col-header-gap"></span>
        <span class="km-col-label">SENDER</span>
        <span class="km-col-label-sub">SUBJECT</span>
    </div>`;
    const actionBar = (kmFolder === 'inbox' && kmSelectedItems.size > 0)
        ? `<div class="km-action-bar">
            <span class="km-action-count">${kmSelectedItems.size} selected</span>
            <button class="km-action-trash" onclick="kmTrashSelected()">🗑 TRASH SELECTED</button>
            <button class="km-action-clear" onclick="kmClearSelection()">CLEAR</button>
        </div>`
        : '';
    const preGameNotice = (gameDay < 1 && kmFolder === 'inbox')
        ? `<div class="km-pregame">✦ The story starts on ${window.KONMAIL_STORY_START}. Spam and daily mail still work in the meantime.</div>`
        : '';
    panel.innerHTML = (rows || preGameNotice)
        ? `${statusBar}${preGameNotice}${actionBar}${colHeader}<div class="km-list">${rows}</div>`
        : `${statusBar}<div class="km-empty">No messages here.</div>`;
}

// ═══════════════════════════════════════════════
//   Detail view
// ═══════════════════════════════════════════════
function kmOpenEmail(type, ref) {
    kmOpenType = type;
    kmOpenRef  = ref;

    let email = null;
    if (type === 'story') {
        email = (window.KONMAIL_STORY || []).find(e => e.id === ref);
        if (email && !S.mailStoryRead.includes(ref)) {
            S.mailStoryRead.push(ref);
            scheduleSave();
        }
    } else if (type === 'daily') {
        email = window.KONMAIL_DAILY_POOL[ref];
        kmDailyRead.add(ref);
        const _rid = email?.id;
        if (_rid && !S.mailDailyState.readIds.includes(_rid)) {
            S.mailDailyState.readIds.push(_rid);
            scheduleSave();
        }
    } else if (type === 'spam') {
        email = kmSpamFindAny(ref);
        if (email && !(S.mailSpamState.readIds || []).includes(ref)) {
            S.mailSpamState.readIds.push(ref);
            scheduleSave();
        }
    } else if (type === 'trash') {
        email = kmTrash.find(t => t.trashId === ref);
    }
    if (!email) return;

    kmRenderPortrait(email);
    kmUpdateBadge();
    kmRenderDetail(type, ref, email);
}

function kmRenderDetail(type, ref, email) {
    if (!type)          type  = kmOpenType;
    if (ref === undefined) ref = kmOpenRef;
    if (!email) {
        if (type === 'story')  email = (window.KONMAIL_STORY || []).find(e => e.id === ref);
        if (type === 'daily')  email = window.KONMAIL_DAILY_POOL[ref];
        if (type === 'spam')   email = kmSpamFindAny(ref);
        if (type === 'trash')  email = kmTrash.find(t => t.trashId === ref);
    }
    if (!email) return;

    // Day 11 ending email gets a special full-panel render.
    if (type === 'story' && email.ending) {
        kmRenderEnding(email);
        return;
    }

    const panel = document.getElementById('km-panel');
    if (!panel) return;

    // Resolve variant body + choices for story emails.
    const isStory = type === 'story';
    const body    = isStory ? kmEffectiveBody(email)    : (email.body || '');
    const choices = isStory ? kmEffectiveChoices(email) : (email.choices || []);

    const rawIso  = isStory && email.day ? (kmDateForDay(email.day) || '')
                                         : (email.date || todayStr());
    // "Tue May 19", matches the dossier date convention; no ISO bleed.
    const _WD = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const _MO = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    let niceDate = '';
    if (rawIso && /^\d{4}-\d{2}-\d{2}/.test(rawIso)) {
        const d = new Date(rawIso.slice(0, 10) + 'T00:00:00');
        if (!isNaN(d)) niceDate = `${_WD[d.getDay()]} ${_MO[d.getMonth()]} ${d.getDate()}`;
    }
    if (!niceDate) niceDate = isStory && email.day ? ('Day ' + email.day) : rawIso;
    const displayDate = isStory && email.day
        ? `Day ${email.day} · ${niceDate}`
        : niceDate;

    let actionsHtml = '';
    const state = isStory ? kmStoryState(email.id) : 'pending';
    const CHOICE_LETTER = ['A','B','C','D','E','F'];

    // Helper, render the reply-choice list (cream capsules with letter bullets).
    function renderChoices(handler, refArg) {
        return `<div class="km-choices">
            <div class="km-choices-label">Reply</div>
            ${choices.map((c, i) => `
                <button class="km-choice" onclick="${handler}(${refArg}, ${i})">
                    <span class="km-choice-bullet">${CHOICE_LETTER[i] || (i+1)}</span>
                    <span class="km-choice-text">${c.label}</span>
                </button>
            `).join('')}
        </div>`;
    }

    // Helper, "Your reply" record + (optional) sender's same-day follow-up.
    // The followup gets a small Fredoka header so it reads as "this is X
    // responding to you" rather than a stray block of italic prose.
    function renderReplyRecord(chosenLabel, followup) {
        return `<div class="km-reply-record">
                <div class="km-reply-record-label">✓ Your reply</div>
                <div class="km-reply-record-text">"${chosenLabel}"</div>
                <div class="km-reply-record-attrib">- you, ${niceDate}</div>
            </div>
            ${followup ? `<div class="km-followup">
                <div class="km-followup-header">${email.from || 'reply'} · same-day reply</div>
                <div class="km-followup-body">${followup}</div>
            </div>` : ''}`;
    }

    if (type === 'story') {
        if (state === 'expired' || state === 'auto-penalised') {
            actionsHtml = `<div class="km-missed-note">⚠ Window closed, this email arrived on ${niceDate}.${state === 'auto-penalised' ? ' Default outcome applied.' : ''}</div>`;
        } else {
            const madeIdx = S.mailStoryChoices[email.id];
            if (madeIdx !== undefined) {
                const made = choices[madeIdx];
                let followup = made?.followup;
                // Writing spec v4 §05, d09 secret-gate followup append, gated on
                // drops recovery state. Mirrors the kmRenderEnding hideoRomance pattern.
                if (followup
                    && email.id === 'd09_clow_checkin3'
                    && madeIdx === 0
                    && S.documentedPath === true
                    && S.packagePath === 'opened'
                    && S.dropsPulledBy) {
                    if (S.dropsPulledBy === 'player_beat1' || S.dropsPulledBy === 'player_beat2') {
                        followup += ' you caught the drops one. that was scary for a second. but you caught it. that\'s the thing ✦';
                    } else if (S.dropsPulledBy === 'coneja') {
                        followup += ' coneja told me she pulled them. she\'s good. i should have explained better, to her, to you. that\'s on me ✦';
                    }
                }
                actionsHtml = renderReplyRecord(made?.label || 'sent', followup);
            } else if (choices && choices.length) {
                actionsHtml = renderChoices('kmSendReply', `'${email.id}'`);
            } else {
                actionsHtml = `<div class="km-replied">No reply needed.</div>`;
            }
        }
        actionsHtml += `<button class="km-trash-btn" onclick="kmMoveToTrash('story','${email.id}')">🗑 Move to Trash</button>`;
    } else if (type === 'daily') {
        if (kmDailyReplied.has(ref)) {
            const madeIdx    = S.mailDailyState.repliedChoices?.[email.id];
            const madeChoice = madeIdx !== undefined ? email.choices?.[madeIdx] : null;
            actionsHtml = renderReplyRecord(madeChoice?.label || 'sent', madeChoice?.followup);
        } else if (email.choices && email.choices.length) {
            actionsHtml = renderChoices('kmReplyDaily', ref);
        }
        actionsHtml += `<button class="km-trash-btn" onclick="kmMoveToTrash('daily',${ref})">🗑 Move to Trash</button>`;
    } else if (type === 'spam') {
        const repliedIdx = (S.mailSpamState.repliedChoices || {})[email.id];
        if (repliedIdx !== undefined) {
            const made = email.choices?.[repliedIdx];
            actionsHtml = renderReplyRecord(made?.label || 'sent', made?.followup);
        } else if (email.choices && email.choices.length) {
            actionsHtml = renderChoices('kmReplySpam', `'${email.id}'`);
        }
        actionsHtml += `<button class="km-trash-btn" onclick="kmMoveToTrash('spam','${email.id}')">🗑 Move to Trash</button>`;
    } else if (type === 'trash') {
        actionsHtml = `<button class="km-delete-btn" onclick="kmDeletePermanently('${ref}')">✗ Delete Permanently</button>`;
    }

    panel.innerHTML = `
        <div class="km-detail">
            <div class="km-meta-row">
                <div class="km-meta-pill">
                    <span class="km-meta-label">From</span>
                    <span class="km-meta-value">${email.from}</span>
                </div>
                <div class="km-meta-pill">
                    <span class="km-meta-label">Date</span>
                    <span class="km-meta-value">${displayDate}</span>
                </div>
            </div>
            <div class="km-meta-pill km-meta-subject">
                <span class="km-meta-label">Subject</span>
                <span class="km-meta-value">${email.subject}</span>
            </div>
            <div class="km-detail-body">${body}</div>
            <div class="km-actions">${actionsHtml}</div>
        </div>
        <button class="km-back-btn" onclick="kmBack()"><span class="km-back-arrow">◂</span> BACK</button>
    `;
}

// ═══════════════════════════════════════════════
//   Day 11 ending render
// ═══════════════════════════════════════════════
function kmRenderEnding(email) {
    const panel = document.getElementById('km-panel');
    if (!panel) return;

    // Defensive: if a player somehow reaches Day 11 without d09 having closed
    // (e.g. dev day-override skipping straight to 11), lock the ending now.
    kmLockEndingIfNeeded();
    const ending = window.pickKonmailEnding(S);
    if (S.mailEnding !== ending.id) {
        S.mailEnding = ending.id;
        scheduleSave();
    }

    let body = ending.body;
    // Writing spec §03, late-joiner addendum is appended to the closed ending
    // body only, gated on first_login_day >= 7. The closed path already implies
    // the player struggled; the addendum acknowledges the late start without piling on.
    if (ending.id === 'closed'
        && (S.mailFirstLoginDay || 0) >= 7
        && ending.lateJoinerAddendum) {
        body += ending.lateJoinerAddendum;
    }
    // Writing spec v4 §05, drops recovery state recolor (Master Canon v2 §06A).
    // Documented + opened + drops pulled = insert line between "i'll be there."
    // and the photo seed line.
    if (S.mailEnding === 'documented'
        && S.packagePath === 'opened'
        && S.dropsPulledBy) {
        let dropsLine = '';
        if (S.dropsPulledBy === 'player_beat1' || S.dropsPulledBy === 'player_beat2') {
            dropsLine = '\n\nyou caught the drops one. that was the worst possible thing and you fixed it ✦';
        } else if (S.dropsPulledBy === 'coneja') {
            dropsLine = '\n\nconeja saved us from the drops one. she didn\'t know what she was doing. she did the right thing anyway ✦';
        }
        if (dropsLine) {
            body = body.replace(
                'i\'ll be there.\n\nthere\'s something i can hand you',
                'i\'ll be there.' + dropsLine + '\n\nthere\'s something i can hand you'
            );
        }
    }
    // Closed + opened + never pulled = heaviest variant (Canon §06A).
    if (S.mailEnding === 'closed'
        && S.packagePath === 'opened'
        && S.dropsPulled === false) {
        body = body.replace(
            'also something is in the back corridor. yeah i know. we\'ll deal with it.',
            'they restocked while you were here. i should have told you. i\'m sorry ✦'
        );
    }
    // Writing spec v3 §24, when the player romanced Hideo, Clow's finale gets
    // a per-tier p.s. line appended after the main body. Blank line for spacing,
    // before the CTA card (which is rendered separately below).
    if (S.hideoRomance) {
        const HIDEO_ROMANCE_PS = {
            documented: "p.s. hideo mentioned you. twice, actually. he doesn't usually mention people. ✦",
            good:       "p.s. hideo asked when you'd be at the booth. i told him friday. ✦",
            remembers:  "p.s. hideo asked when you'd be at the booth. i told him friday. ✦",
            space:      "p.s. hideo asked when you'd be at the booth. i told him friday. ✦",
            closed:     "p.s. hideo asked after you. i hope you find each other at the booth ✦",
        };
        const ps = HIDEO_ROMANCE_PS[ending.id];
        if (ps) body += `\n\n${ps}`;
    }
    // Writing spec v6 §07, Petra romance p.s. modifier. Additive with the
    // hideoRomance block above: both can fire on the same playthrough. Order
    // matters, hideo first, then petra, blank line between (from the \n\n).
    if (S.petraRomance) {
        const PETRA_ROMANCE_PS = {
            documented: "p.s. petra asked when you'd be at the booth. she didn't tease me about it. that's how i knew ✦",
            good:       "p.s. petra asked when you'd be at the booth. you'll see her again before then though ✦",
            remembers:  "p.s. petra asked when you'd be at the booth. you'll see her again before then though ✦",
            space:      "p.s. petra asked when you'd be at the booth. you'll see her again before then though ✦",
            closed:     "p.s. petra asked after you. she'll be there ✦",
        };
        const ps = PETRA_ROMANCE_PS[ending.id];
        if (ps) body += `\n\n${ps}`;
    }
    const bodyHtml = body.split('\n').map(l => `<p class="km-p">${l || '&nbsp;'}</p>`).join('');
    const dateStr = kmDateForDay(11);

    panel.innerHTML = `
        <div class="km-detail km-ending-screen" style="--ending-accent:${ending.accent}">
            <div class="km-ending-banner">
                <div class="km-ending-icon">${ending.icon}</div>
                <div class="km-ending-titles">
                    <div class="km-ending-label">DAY 11, ENDING</div>
                    <div class="km-ending-name">${ending.name}</div>
                </div>
            </div>
            <div class="km-detail-header">
                <div class="km-detail-field"><span class="km-label">FROM:</span><span class="km-value">${email.from}</span></div>
                <div class="km-detail-field"><span class="km-label">DATE:</span><span class="km-value">${dateStr}</span></div>
                <div class="km-detail-field km-full"><span class="km-label">SUBJECT:</span><span class="km-value">${email.subject}</span></div>
            </div>
            <div class="km-detail-body">${bodyHtml}</div>
            <div class="km-ending-cta">${ending.cta}</div>
        </div>
        <button class="km-back-btn" onclick="kmBack()">← BACK</button>
    `;
}

// ═══════════════════════════════════════════════
//   Reply actions
// ═══════════════════════════════════════════════
function kmSendReply(storyId, choiceIdx) {
    const email = (window.KONMAIL_STORY || []).find(e => e.id === storyId);
    if (!email) return;
    if (S.mailStoryChoices[storyId] !== undefined) return;

    const state = kmStoryState(storyId);
    if (state === 'expired' || state === 'auto-penalised') return;

    const choices = kmEffectiveChoices(email);
    const choice = choices[choiceIdx];
    if (!choice) return;

    S.mailStoryChoices[storyId] = choiceIdx;
    kmSetStoryState(storyId, 'answered');

    const pts = kmApplyEffects(choice.effects, `mail:story:${storyId}`);
    // Lock the ending the moment Clow's Day-9 check-in closes. Reading
    // S.mailEnding from d11 variants thereafter is deterministic.
    if (storyId === 'd09_clow_checkin3') kmLockEndingIfNeeded();
    scheduleSave();
    if (pts) SFX.play('pts');
    else     SFX.play('open');
    toast(pts > 0 ? `✉ Sent! +${pts} pts` : `✉ Sent.`);
    if (window.triggerMission) window.triggerMission('mail_reply');
    kmRenderDetail('story', storyId, email);
}

function kmReplyDaily(idx, choiceIdx) {
    if (kmDailyReplied.has(idx)) return;
    kmDailyReplied.add(idx);
    const email = window.KONMAIL_DAILY_POOL[idx];
    const pts   = email?.choices?.[choiceIdx]?.pts || 3;
    const _rid  = email?.id;
    if (_rid && S.mailDailyState.repliedChoices[_rid] === undefined) {
        S.mailDailyState.repliedChoices[_rid] = choiceIdx;
        scheduleSave();
    }
    addPts(pts, `mail:daily:${_rid || idx}`);
    SFX.play('pts');
    toast(`✉ Sent! +${pts} pts`);
    if (window.triggerMission) window.triggerMission('mail_reply');
    kmRenderDetail('daily', idx, email);
}

function kmReplySpam(spamId, choiceIdx) {
    const email = kmSpamFindAny(spamId);
    if (!email || !email.choices) return;
    const ss = S.mailSpamState;
    if (ss.repliedChoices[spamId] !== undefined) return;
    const choice = email.choices[choiceIdx];
    if (!choice) return;

    ss.repliedChoices[spamId] = choiceIdx;
    if (!ss.readIds.includes(spamId)) ss.readIds.push(spamId);

    if (choice.chain && !ss.unlockedChainIds.includes(choice.chain)) {
        ss.unlockedChainIds.push(choice.chain);
    }
    if (choice.important && !ss.importantHandled.includes(choice.important)) {
        ss.importantHandled.push(choice.important);
        const idx = S.mailImportantTrashed.indexOf(choice.important);
        if (idx >= 0) S.mailImportantTrashed.splice(idx, 1);
    }
    if (choice.importantTrash && !S.mailImportantTrashed.includes(choice.importantTrash)) {
        S.mailImportantTrashed.push(choice.importantTrash);
    }

    const pts = choice.pts || 0;
    if (pts !== 0) {
        addPts(pts, `mail:spam:${spamId}`);
        SFX.play('pts');
        toast(pts > 0 ? `✉ Sent! +${pts} pts` : `✉ Sent... ${pts} pts`);
    } else {
        SFX.play('open');
        toast('✉ Sent.');
    }
    scheduleSave();
    if (window.triggerMission) window.triggerMission('spam_reply');
    kmRenderDetail('spam', spamId, email);
    kmUpdateBadge();
}

function kmMoveToTrash(type, ref) {
    let email = null;
    if (type === 'daily') {
        email = window.KONMAIL_DAILY_POOL[ref];
        kmDailyTrashed.add(ref);
        const _tid = email?.id;
        if (_tid && !S.mailDailyState.trashedIds.includes(_tid)) {
            S.mailDailyState.trashedIds.push(_tid);
            scheduleSave();
        }
    } else if (type === 'story') {
        email = (window.KONMAIL_STORY || []).find(e => e.id === ref);
        if (!email) return;
        if (!S.mailStoryRead.includes(ref)) S.mailStoryRead.push(ref);
        if (!S.mailStoryTrashed.includes(ref)) S.mailStoryTrashed.push(ref);
        scheduleSave();
    } else if (type === 'spam') {
        email = kmSpamFindAny(ref);
        if (!email) return;
        const ss = S.mailSpamState;
        if (!ss.trashedIds.includes(ref)) ss.trashedIds.push(ref);
        const wasRead = (ss.readIds || []).includes(ref);
        addPts(3, 'mail:spam-trash');
        SFX.play('pts');
        toast('🗑 Spam deleted! +3 pts');
        if (email.important && !wasRead) {
            if (!S.mailImportantTrashed.includes(email.importantId)) {
                S.mailImportantTrashed.push(email.importantId);
            }
            toast('⚠ Wait... that might have been important.');
        }
        scheduleSave();
    }
    if (!email) return;
    const trashId = `t_${type}_${ref}_${Date.now()}`;
    kmTrash.push({
        trashId,
        type, ref,
        from: email.from,
        subject: email.subject,
        body: email.body,
        date: email.date || kmDateForDay(email.day) || todayStr(),
        portraitEmoji: email.portraitEmoji,
        portraitColor: email.portraitColor,
    });
    kmBack();
}

function kmDeletePermanently(trashId) {
    kmTrash = kmTrash.filter(t => t.trashId !== trashId);
    kmBack();
}

function kmToggleSelect(kind, ref) {
    const key = `${kind}:${ref}`;
    if (kmSelectedItems.has(key)) kmSelectedItems.delete(key);
    else kmSelectedItems.add(key);
    kmRender();
}
function kmClearSelection() {
    kmSelectedItems.clear();
    kmRender();
}
function kmTrashSelected() {
    if (kmSelectedItems.size === 0) return;
    const keys = [...kmSelectedItems];
    const stamp = Date.now();
    let count = 0;
    for (const key of keys) {
        const sep = key.indexOf(':');
        const kind = key.slice(0, sep);
        const rawRef = key.slice(sep + 1);
        let email = null;
        let ref;
        if (kind === 'daily') {
            ref = Number(rawRef);
            email = window.KONMAIL_DAILY_POOL[ref];
            if (!email) continue;
            kmDailyTrashed.add(ref);
            if (!S.mailDailyState.trashedIds.includes(email.id)) {
                S.mailDailyState.trashedIds.push(email.id);
            }
        } else if (kind === 'story') {
            ref = rawRef;
            email = (window.KONMAIL_STORY || []).find(e => e.id === ref);
            if (!email) continue;
            if (!S.mailStoryRead.includes(ref)) S.mailStoryRead.push(ref);
            if (!S.mailStoryTrashed.includes(ref)) S.mailStoryTrashed.push(ref);
        } else {
            continue;
        }
        kmTrash.push({
            trashId: `t_${kind}_${ref}_${stamp}_${Math.random().toString(36).slice(2, 6)}`,
            type: kind,
            ref,
            from: email.from,
            subject: email.subject,
            body: email.body,
            date: email.date || kmDateForDay(email.day) || todayStr(),
            portraitEmoji: email.portraitEmoji,
            portraitColor: email.portraitColor,
        });
        count++;
    }
    if (count > 0) {
        scheduleSave();
        SFX.play('open');
        toast(`🗑 ${count} email${count > 1 ? 's' : ''} trashed`);
    }
    kmSelectedItems.clear();
    kmRender();
}

// ── Dev override helper (call from DevTools console) ──
function kmSetDayOverride(n) {
    if (n == null) {
        delete window.__kmDayOverride;
        console.log('[konmail] day override cleared. Real calendar resumed.');
    } else {
        window.__kmDayOverride = Number(n);
        console.log(`[konmail] day override set: Day ${n} (date ${kmDateForDay(Number(n)) || 'out of range'})`);
    }
    if (document.getElementById('w-konmail')) {
        kmCatchUp();
        kmRender();
    }
}

// ── Expose ──
window.openKonmail          = openKonmail;
window.kmSetFolder          = kmSetFolder;
window.kmOpenEmail          = kmOpenEmail;
window.kmSendReply          = kmSendReply;
window.kmReplyDaily         = kmReplyDaily;
window.kmReplySpam          = kmReplySpam;
window.kmRefreshBadges      = kmRefreshBadges;
window.kmMoveToTrash        = kmMoveToTrash;
window.kmDeletePermanently  = kmDeletePermanently;
window.kmToggleSelect       = kmToggleSelect;
window.kmClearSelection     = kmClearSelection;
window.kmTrashSelected      = kmTrashSelected;
window.kmBack               = kmBack;
window.kmSetDayOverride     = kmSetDayOverride;
window.kmGameDay            = kmGameDay;
